import { createTheme } from "@mui/material/styles";
import React from "react";

declare module "@mui/material/styles" {
    export interface Theme {
        palette: {
            primary: {
                main: string;
                light: string;
                dark: string;
                darker: string;
            };
            secondary: {
                main: string;
                dark: string;
                light: string;
            };
            error: {
                main: string;
                light: string;
                lighter: string;
                background: string;
            };
            warning: {
                main: string;
                dark: string;
                light: string;
                lighter: string;
            };
            success: {
                main: string;
                light: string;
            };
            info: {
                main: string;
                light: string;
                lighter: string;
            };
            text: {
                primary: string;
                secondary: string;
            };
            border: {
                main: string;
                tabs?: string;
                select?: string;
                divider?: string;
                verticalDivider?: string;
            };
            disabled: {
                thumb: string;
                track: string;
            };
            table: {
                main: string;
            };
            graphs: {
                main: string;
                dots: string;
            };
            hmi: {
                pausedBackground: string;
                pausedColor: string;
            };
            corporateBO: {
                lineSeparator: string;
                checkbox: string;
                checkboxTrack: string;
                checkboxTrackChecked: string;
                bottleneckCardBackground: string;
                buttonLineDivider: string;
                tabText: string;
            };
            hmiCodePrintPreview: {
                backgroundColor: string;
            };
            backgrounds: {
                main: string;
            };
            dropShadows: {
                main: string;
            };
            menu: {
                icons: {
                    main: string;
                };
            };
        };
        typography: {
            h1: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            h2: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            h3: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            bigHMIDropdown: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            subtitle1: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            subtitle2: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            error: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            h5: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            h6: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            rcaTitle: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
        };
    }
}

declare module "@mui/material/styles" {
    interface TypographyVariants {
        error: React.CSSProperties;
        bigHMIDropdown: React.CSSProperties;
        rcaTitle: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        error?: React.CSSProperties;
        bigHMIDropdown?: React.CSSProperties;
        rcaTitle: React.CSSProperties;
    }

    interface Palette {
        border: Palette["primary"];
        table: Palette["primary"];
        graphs: Palette["primary"];
        hmi: {
            pausedBackground: string;
            pausedColor: string;
        };
        corporateBO: {
            lineSeparator: string;
            checkbox: string;
            checkboxTrack: string;
            checkboxTrackChecked: string;
            bottleneckCardBackground: string;
            buttonLineDivider: string;
            tabText: string;
        };
        hmiCodePrintPreview: {
            backgroundColor: string;
        };
        disabled: {
            thumb: string;
            track: string;
        };
    }

    interface PaletteOptions {
        border:
            | PaletteOptions["primary"]
            | { tabs?: string; select?: string; divider?: string; verticalDivider?: string };
        table: PaletteOptions["primary"];
        graphs: PaletteOptions["primary"] | { dots?: string };
        hmi: {
            pausedBackground: string;
            pausedColor: string;
        };
        corporateBO: {
            lineSeparator: string;
            checkbox: string;
            checkboxTrack: string;
            checkboxTrackChecked: string;
            bottleneckCardBackground: string;
            buttonLineDivider: string;
            tabText: string;
        };
        hmiCodePrintPreview: {
            backgroundColor: string;
        };
        disabled?: {
            thumb: string;
            track: string;
        };
        backgrounds: PaletteOptions["primary"];
        dropShadows: PaletteOptions["primary"];
        menu: {
            icons: {
                main: string;
            };
        };
    }

    interface PaletteColor {
        darker?: string;
        lighter?: string;
    }

    interface SimplePaletteColorOptions {
        darker?: string;
        lighter?: string;
        background?: string;
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        h1: true;
        h2: true;
        h3: true;
        bigHMIDropdown: true;

        rcaTitle: true;
        subtitle1: true;
        subtitle2: true;
        error: true;
        h5: true;
        h6: true;
    }
}

// ---------------------------------------------------------------------------
// CEDAR REBRAND NOTES (for Band of Coders):
//  - primary.main (#FFFFFF) and secondary.main were used as SURFACE/background
//    colors in ~40 files (78 refs), so they are intentionally KEPT light. The
//    brand red (#EF3340) is introduced via the `info` accent channel + the
//    MuiButton override below, NOT by flipping primary/secondary.
//  - Brand tokens: red #EF3340 / dark #C41E2A, ink #06233F, page bg #F5F5F5,
//    radius 10, font "Source Sans 3".
// ---------------------------------------------------------------------------
const BRAND_RED = "#EF3340";
const BRAND_RED_DARK = "#C41E2A";
const BRAND_INK = "#06233F";
// Source Sans 3 is self-hosted (src/fonts/SourceSans3 + @font-face in index.css), so it is
// always available — including on offline HMIs. "Myriad Pro" was dropped from the fallback
// chain: it's a commercial Adobe font we can't bundle, so it only ever resolved for users
// who happened to have it installed, making offline rendering inconsistent.
const FONT_STACK = '"Source Sans 3", system-ui, sans-serif';

export const osornoTheme = createTheme({
    palette: {
        primary: {
            main: "#FFFFFF",
            light: "#FAFAFA",
            dark: "#8C93B1",
            darker: "#000000",
        },
        secondary: {
            main: "#F5F5F5",
            dark: "#292F37",
            light: "#FAFAFA",
        },
        error: {
            main: "#C64A4A",
            light: "#D76565",
            lighter: "#F6E4E4",
            background: "#FDD0D0",
        },
        warning: {
            main: "#EF8027",
            dark: "#F9C13D",
            light: "#F5A936",
            lighter: "#EED9A3",
        },
        success: {
            main: "#3CB371",
            light: "#4DC381",
        },
        text: {
            primary: BRAND_INK,
            secondary: "#5B6B7F",
        },
        info: {
            main: BRAND_RED,
            light: BRAND_RED_DARK,
            lighter: "#F7CDD1",
        },
        border: {
            main: "#D8D8D8",
            tabs: BRAND_RED,
            select: "#E0E0E0",
            divider: "#556482",
            verticalDivider: "#E0E0E0",
        },
        disabled: {
            thumb: "#BDBDBD",
            track: "#E0E0E0",
        },
        table: {
            main: "#F5F5F5",
        },
        graphs: {
            main: "#eeeff1",
            dots: "#D4D6E1",
        },
        hmi: {
            pausedBackground: "#ffd203",
            pausedColor: "#000000",
        },
        corporateBO: {
            lineSeparator: "#E0E0E0",
            checkbox: "#858585",
            checkboxTrack: "#D1D4E0",
            checkboxTrackChecked: "#F2B8BD",
            bottleneckCardBackground: "#FEF2E5",
            buttonLineDivider: BRAND_RED,
            tabText: "#000000",
        },
        hmiCodePrintPreview: {
            backgroundColor: BRAND_INK,
        },
        menu: {
            icons: {
                main: "#2C2F41",
            },
        },
        backgrounds: {
            main: "#F5F5F5",
        },
        dropShadows: {
            main: "#06233F1A",
        },
    },
    shape: {
        borderRadius: 10,
    },
    typography: {
        fontFamily: FONT_STACK,
        h1: {
            fontFamily: FONT_STACK,
            fontSize: 28,
            fontWeight: 700,
        },
        h2: {
            fontFamily: FONT_STACK,
            fontWeight: 700,
            fontSize: 24,
        },
        h3: {
            fontFamily: FONT_STACK,
            fontSize: 20,
            fontWeight: 600,
        },
        bigHMIDropdown: {
            fontFamily: FONT_STACK,
            fontSize: 22,
            fontWeight: 400,
        },
        subtitle1: {
            fontFamily: FONT_STACK,
            fontSize: 16,
            fontWeight: 400,
        },
        subtitle2: {
            fontFamily: FONT_STACK,
            fontSize: 14,
            fontWeight: 700,
        },
        error: {
            fontFamily: FONT_STACK,
            fontSize: 14,
            fontWeight: 400,
        },
        h5: {
            fontFamily: FONT_STACK,
            fontSize: 16,
            fontWeight: 700,
        },
        h6: {
            fontFamily: FONT_STACK,
            fontSize: 10,
            fontWeight: 400,
        },
        rcaTitle: {
            fontFamily: FONT_STACK,
            fontSize: "24px",
            fontWeight: 700,
        },
    },
    spacing: 4,
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                },
                // Primary CTAs adopt the brand red. color="secondary"/"error"
                // contained buttons keep their own colors.
                containedPrimary: {
                    backgroundColor: BRAND_RED,
                    color: "#FFFFFF",
                    boxShadow: "none",
                    "&:hover": {
                        backgroundColor: BRAND_RED_DARK,
                        boxShadow: "0 6px 16px rgba(239,51,64,0.28)",
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    border: "1px solid rgba(6,35,63,0.10)",
                    boxShadow: "0 14px 34px rgba(6,35,63,0.08)",
                },
            },
        },
        // Cedar-style pill tabs (app-wide): no underline indicator; selected = red pill.
        MuiTabs: {
            styleOverrides: {
                root: { minHeight: 40 },
                indicator: { display: "none" },
            },
        },
        MuiTab: {
            // !important is required: ~20 page-level tab style classes use a high-specificity selector
            // (".MuiTabs-flexContainer .MuiButtonBase-root { padding: 0 }") plus a bottom-border underline
            // that would otherwise beat this single-class theme override. !important wins over their
            // non-important rules, giving consistent Cedar pills everywhere from one place.
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "999px",
                    minHeight: 38,
                    margin: "0 4px",
                    padding: "8px 24px !important",
                    borderBottom: "none !important",
                    color: "#2C2F41",
                    "&.Mui-selected": {
                        backgroundColor: `${BRAND_RED} !important`,
                        color: "#FFFFFF !important",
                        borderBottom: "none !important",
                        boxShadow: "0 4px 12px rgba(239,51,64,0.20)",
                    },
                },
            },
        },
    },
});
